import React, { useState, useEffect } from 'react';
import { Button, Avatar, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useUser } from '../contexts/UserContext';
import { useLogout } from '../hooks/useLogout';
import { clearRedirectUrl } from '../utils/redirectHelpers';
import axios from 'axios';

interface UserInfo {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface NavbarProps {
  isAuthenticated: boolean;
  onLogout: () => void;
}

function getInitials(user: UserInfo) {
  const { firstName, lastName, email } = user;
  if (firstName && lastName) {
    return (firstName[0] + lastName[0]).toUpperCase();
  }
  if (firstName) return firstName[0].toUpperCase();
  if (lastName) return lastName[0].toUpperCase();
  if (email) {
    const login = email.split('@')[0];
    if (login.length >= 2) return login.slice(0, 2).toUpperCase();
    if (login.length === 1) return login[0].toUpperCase();
    return '?';
  }
  return '?';
}

const NavbarC: React.FC<NavbarProps> = ({ isAuthenticated, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { handleLogout } = useLogout(onLogout);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const mobileMenu = document.getElementById('mobile-menu');
      const mobileMenuButton = document.getElementById('mobile-menu-button');
      const userDropdown = document.getElementById('user-dropdown');
      const userAvatar = document.getElementById('user-avatar');
      
      // Close mobile menu
      if (isMobileMenuOpen && 
          mobileMenu && 
          mobileMenuButton &&
          !mobileMenu.contains(target) && 
          !mobileMenuButton.contains(target)) {
        setIsMobileMenuOpen(false);
      }

      // Close user dropdown
      if (isUserDropdownOpen &&
          userDropdown &&
          userAvatar &&
          !userDropdown.contains(target) && 
          !userAvatar.contains(target)) {
        setIsUserDropdownOpen(false);
      }
    };

    if (isMobileMenuOpen || isUserDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when mobile menu is open
      if (isMobileMenuOpen) {
        document.body.style.overflow = 'hidden';
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isUserDropdownOpen]);

  // Handle window resize to close mobile menu and restore scroll
  useEffect(() => {
    const handleResize = () => {
      // Close mobile menu when window becomes wide enough
      if (window.innerWidth >= 1280 && isMobileMenuOpen) { // xl breakpoint is 1280px
        setIsMobileMenuOpen(false);
        document.body.style.overflow = 'unset';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileMenuOpen]);

  const handleProfileClick = () => {
    navigate('/profile');
    setIsUserDropdownOpen(false);
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setIsUserDropdownOpen(false);
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const handleLoginClick = () => {
    clearRedirectUrl();
    navigate('/login');
  };

  const handleRegisterClick = () => {
    clearRedirectUrl();
    navigate('/register');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = (path: string) => {
    if (path === '/login' || path === '/register') clearRedirectUrl();
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <div 
              className="cursor-pointer flex items-center select-none"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
              }}
            >
              <img 
                src="https://sonicmakers-uploads.lon1.digitaloceanspaces.com/public/logos/noBgWhite.png" 
                alt={t('navigation.vst_shop')}
                className="h-16 w-auto select-none pointer-events-none"
                draggable={false}
              />
            </div>
            <nav className="hidden xl:flex space-x-6">
              <button 
                onClick={() => navigate('/plugins')}
                className="text-gray-300 hover:text-white transition-colors font-medium"
              >
                {t('navigation.plugins')}
              </button>
              <button 
                onClick={() => navigate('/news')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                {t('navigation.news')}
              </button>
              <button 
                onClick={() => navigate('/account/downloads')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                {t('navigation.downloads')}
              </button>
              <button 
                onClick={() => navigate('/about')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                {t('about.page_title')}
              </button>
              <button 
                onClick={() => navigate('/contacts')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                {t('contacts.page_title')}
              </button>
            </nav>
          </div>
          
          {/* Right side controls */}
          <div className="flex items-center gap-2">
            {/* Desktop auth buttons */}
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2 }}>
              {isAuthenticated && user ? (
                <div className="relative">
                  <Avatar
                    id="user-avatar"
                    sx={{ 
                      bgcolor: 'linear-gradient(45deg, #a855f7, #ec4899)', 
                      cursor: 'pointer',
                      width: 64,
                      height: 64,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #a855f7, #ec4899)'
                    }}
                    onClick={toggleUserDropdown}
                  >
                    {getInitials(user)}
                  </Avatar>

                  {/* User dropdown menu */}
                  {isUserDropdownOpen && (
                    <div 
                      id="user-dropdown"
                      className="absolute right-0 w-64 bg-gray-800/95 backdrop-blur-lg border border-gray-700 rounded-lg shadow-lg z-50"
                      style={{ top: '80px' }}
                    >
                      <div className="py-2">
                                                  <div className="px-4 py-2 border-b border-gray-700">
                            <div className="text-gray-300 font-bold">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-gray-400 text-xs">
                              {user.email}
                            </div>
                          </div>
                        
                        <button
                          onClick={handleProfileClick}
                          className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                        >
                          {t('navigation.my_profile')}
                        </button>
                        
                        <button
                          onClick={() => {
                            navigate('/account/products');
                            setIsUserDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                        >
                          {t('navigation.my_products')}
                        </button>
                        <button
                          onClick={() => {
                            navigate('/account/computers');
                            setIsUserDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                        >
                          {t('navigation.computers')}
                        </button>
                        
                        <button
                          onClick={() => {
                            navigate('/personal-offers');
                            setIsUserDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                        >
                          {t('navigation.personal_offers')}
                        </button>
                        
                        <button
                          onClick={() => {
                            navigate('/account/downloads');
                            setIsUserDropdownOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                        >
                          {t('navigation.downloads')}
                        </button>
                        
                        {user.role === 'admin' && (
                          <button
                            onClick={handleAdminClick}
                            className="w-full text-left px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700/50 transition-colors"
                          >
                            {t('navigation.admin_panel')}
                          </button>
                        )}
                        
                        <div className="border-t border-gray-700 mt-1 pt-1">
                          <button
                            onClick={() => {
                              handleLogout();
                              setIsUserDropdownOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-red-400 hover:text-red-300 hover:bg-gray-700/50 transition-colors"
                          >
                            {t('auth.logout_button')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={handleLoginClick}
                    className="text-gray-300 hover:text-white transition-colors px-3 py-2 rounded-md hover:bg-purple-500/20"
                  >
                    {t('auth.login_button_nav')}
                  </button>
                  <button
                    onClick={handleRegisterClick}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-2 rounded-md transition-all duration-300"
                  >
                    {t('auth.register_button_nav')}
                  </button>
                </>
              )}
            </Box>

            {/* Mobile menu button - shown only for non-authenticated users */}
            {!isAuthenticated && (
              <button
                id="mobile-menu-button"
                onClick={toggleMobileMenu}
                className="xl:hidden text-white p-2 rounded-md hover:bg-purple-500/20 transition-colors"
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}

            {/* Mobile avatar for authenticated users - includes hamburger functionality */}
            {isAuthenticated && user && (
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center' }}>
                <div className="relative">
                  <Avatar
                    id="user-avatar"
                    sx={{ 
                      bgcolor: 'linear-gradient(45deg, #a855f7, #ec4899)', 
                      cursor: 'pointer',
                      width: 64,
                      height: 64,
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      background: 'linear-gradient(45deg, #a855f7, #ec4899)'
                    }}
                    onClick={toggleMobileMenu}
                  >
                    {getInitials(user)}
                  </Avatar>
                </div>
              </Box>
            )}
          </div>
        </div>
        
        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="xl:hidden bg-black/95 backdrop-blur-lg border-t border-gray-800 fixed inset-x-0 top-[96px] z-40">
            <div className="px-4 py-6 space-y-4">
              {/* Navigation links */}
              <button 
                onClick={() => handleMobileNavClick('/plugins')}
                className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
              >
                {t('navigation.plugins')}
              </button>
              <button 
                onClick={() => handleMobileNavClick('/news')}
                className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
              >
                {t('navigation.news')}
              </button>
              <button 
                onClick={() => handleMobileNavClick('/about')}
                className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
              >
                {t('about.page_title')}
              </button>
              <button 
                onClick={() => handleMobileNavClick('/contacts')}
                className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
              >
                {t('contacts.page_title')}
              </button>
              
              {/* Divider */}
              <div className="border-t border-gray-700 pt-4"></div>
              
              {/* Auth section */}
              {isAuthenticated && user ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 py-2">
                    <Avatar
                      sx={{ 
                        bgcolor: 'linear-gradient(45deg, #a855f7, #ec4899)', 
                        width: 48,
                        height: 48,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        background: 'linear-gradient(45deg, #a855f7, #ec4899)'
                      }}
                    >
                      {getInitials(user)}
                    </Avatar>
                    <div>
                      <div className="text-white font-medium">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-gray-400 text-sm">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleMobileNavClick('/profile')}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                  >
                    {t('navigation.my_profile')}
                  </button>
                  
                  <button
                    onClick={() => handleMobileNavClick('/account/products')}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                  >
                    {t('navigation.my_products')}
                  </button>
                  <button
                    onClick={() => handleMobileNavClick('/account/computers')}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                  >
                    {t('navigation.computers')}
                  </button>
                  
                  <button
                    onClick={() => handleMobileNavClick('/personal-offers')}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                  >
                    {t('navigation.personal_offers')}
                  </button>
                  
                  <button
                    onClick={() => handleMobileNavClick('/account/downloads')}
                    className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                  >
                    {t('navigation.downloads')}
                  </button>
                  
                  {user.role === 'admin' && (
                    <button
                      onClick={() => handleMobileNavClick('/admin')}
                      className="block w-full text-left text-gray-300 hover:text-white transition-colors py-2 text-lg"
                    >
                      {t('navigation.admin_panel')}
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left text-red-400 hover:text-red-300 transition-colors py-2 text-lg"
                  >
                    {t('auth.logout_button')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={() => handleMobileNavClick('/login')}
                    className="block w-full text-center text-gray-300 hover:text-white transition-colors py-3 text-lg border border-gray-600 rounded-md"
                  >
                    {t('auth.login_button_nav')}
                  </button>
                  <button
                    onClick={() => handleMobileNavClick('/register')}
                    className="block w-full text-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-md transition-all duration-300 text-lg font-medium"
                  >
                    {t('auth.register_button_nav')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default NavbarC; 